import { ExpandedState, SortingState } from '@tanstack/react-table';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { getPastFlightsForCampaign } from 'src/components/SponsoredListings/SponsoredListings.util';
import {
  CampaignSalesAggregate,
  useAdCampaignsDataContext,
} from 'src/contexts/AdCampaignsDataContext';
import { useCatalogDataContext } from 'src/contexts/CatalogDataContext/useCatalogDataContext';
import { ColumnResizingContextProvider } from 'src/contexts/ColumnResizingContext/ColumnResizingContext';
import {
  Content,
  getFormattedContent,
  useContent,
  useContentContext,
} from 'src/contexts/ContentContext';
import { useLocalizationContext } from 'src/contexts/LocalizationContext';
import { PosSpinner } from 'src/core/POS/PosSpinner';
import { vars } from 'src/core/themes';
import { NoData } from 'src/tables/Table';
import { GroupedVirtuosoTable } from 'src/tables/TableV2/GroupedVirtuosoTable';
import { ContentId } from 'src/utils/constants/contentId';
import { FormatContentId } from 'src/utils/constants/formatContentId';
import {
  getEventPerformerVenue,
  getFormattedEventName,
} from 'src/utils/eventWithDataUtils';
import { SponsoredListingsCampaign } from 'src/WebApiController';

import * as styles from '../SponsoredListingsTable.css';
import {
  CampaignsTableRow,
  getTableColumnDefinitions,
} from './CampaignsTableV2ColumnsConfig';

export type CampaignsTableV2Props = {
  isLoading: boolean;
  disabled?: boolean;
  campaigns: SponsoredListingsCampaign[] | null | undefined;
  onStartWork: () => void;
  onStopWork: (success: boolean) => void;
  showEventColumns?: boolean;
  hideActive?: boolean;
  hidePrevious?: boolean;
  hideOlder?: boolean;
};

export const CampaignsTableV2: React.FC<CampaignsTableV2Props> = ({
  isLoading,
  disabled,
  campaigns,
  onStartWork,
  onStopWork,
  showEventColumns,
  hideActive,
  hidePrevious,
  hideOlder,
}) => {
  const { data } = useCatalogDataContext();
  const { getUiCurrency } = useLocalizationContext();
  const { campaignSalesAggregates } = useAdCampaignsDataContext();

  const columns = useMemo(() => {
    const columns = getTableColumnDefinitions(
      onStartWork,
      onStopWork,
      disabled
    );
    if (!showEventColumns) {
      return columns.filter(
        (column) => column.id !== 'eventName' && column.id !== 'eventDate'
      );
    }
    return columns;
  }, [onStartWork, onStopWork, disabled, showEventColumns]);

  const [expandedState, setExpandedState] = useState<ExpandedState>({});
  const [sortState, setSortState] = useState<SortingState>([]);

  const contentContext = useContentContext();
  const currentFlightTex = useContent(ContentId.CurrentFlight);
  const previousFlightText = useContent(ContentId.PreviousFlight);
  const olderFlightsText = useContent(ContentId.OlderFlights);

  const getSubRows = useCallback(
    (data: CampaignsTableRow) => {
      if (data.campaign) {
        const rows = [];

        if (!hideActive && data.campaign.activeFlight) {
          rows.push({
            id: data.id + '-active',
            campaignId: data.campaignId,
            name: `• ${currentFlightTex}`,
            hasChildren: false,
            flight: data.campaign.activeFlight,
            eventId: data.campaign.eventId,
            currency: data.currency,
            salesData: data.salesData,
          } as CampaignsTableRow);
        }

        const pastFlights = getPastFlightsForCampaign(data.campaign);
        if (pastFlights && pastFlights.length > 0) {
          const prevFlight = pastFlights[0];
          if (!hidePrevious) {
            rows.push({
              id: data.id + '-previous',
              campaignId: data.campaignId,
              name: `• ${previousFlightText}`,
              hasChildren: false,
              flight: prevFlight,
              currency: data.currency,
              salesData: data.salesData,
            } as CampaignsTableRow);
          }
          if (!hideOlder && pastFlights.length > 1) {
            rows.push({
              id: data.id + '-passed',
              campaignId: data.campaignId,
              name: `• ${olderFlightsText} (${pastFlights.length})`,
              hasChildren: true,
              flights: pastFlights.slice(1),
              currency: data.currency,
              salesData: data.salesData,
            } as CampaignsTableRow);
          }
        }
        return rows;
      }

      if (data.flights && data.flights.length > 0) {
        return data.flights.map(
          (flight, index) =>
            ({
              id: flight.flightId.toString(),
              campaignId: data.campaignId,
              name: getFormattedContent(
                FormatContentId.FlightNumberX,
                [`${index + 1}`],
                contentContext
              ),
              hasChildren: false,
              flight: flight,
              currency: data.currency,
              salesData: data.salesData,
            }) as CampaignsTableRow
        );
      }

      return [];
    },
    [
      hideActive,
      currentFlightTex,
      hidePrevious,
      hideOlder,
      previousFlightText,
      olderFlightsText,
      contentContext,
    ]
  );

  const tableData = useMemo<CampaignsTableRow[]>(
    () =>
      (campaigns ?? [])
        .toSorted((a, b) => {
          // Sort by campaign date desc

          const aDate = a.dateRange.start
            ? new Date(a.dateRange.start).getTime()
            : 0;
          const bDate = b.dateRange.start
            ? new Date(b.dateRange.start).getTime()
            : 0;

          return bDate - aDate;
        })
        .map((c) => {
          const { event, venue, performer } = getEventPerformerVenue(
            c.eventId.toString(),
            data
          );

          const [eventName, eventNameConnector, eventSubName] =
            getFormattedEventName({
              event: event?.event,
              performer,
              venue,
              slimMode: false,
            });

          const salesData = campaignSalesAggregates?.[c.campaignId];
          const currency = getUiCurrency(
            salesData?.currencyCode || c.currencyCode || 'USD'
          );

          return {
            id: c.campaignId.toString(),
            campaignId: c.campaignId,
            name: c.campaignName,
            campaign: c,
            eventId: c.eventId,
            eventName: `${eventName}${eventNameConnector}${eventSubName}`,
            eventNameParts: [eventName, eventNameConnector, eventSubName],
            eventDate: event?.event?.dates.start
              ? new Date(event.event.dates.start)
              : undefined,
            currency: currency,
            salesData: salesData,
            hasChildren: Boolean(
              c.activeFlight ||
              Boolean(c.flights?.length) ||
              Boolean(getPastFlightsForCampaign(c)?.length)
            ),
          } as CampaignsTableRow;
        }),
    [campaignSalesAggregates, campaigns, data, getUiCurrency]
  );

  // Expand all rows with children on first render
  useEffect(() => {
    const expandedRows: ExpandedState = {};
    tableData.forEach((row) => {
      if (row.hasChildren) {
        expandedRows[row.id] = true;
      }
    });
    setExpandedState(expandedRows);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array ensures this only runs on first render

  const onRowWrapper = useCallback(
    (row: CampaignsTableRow, tableRow: React.ReactNode) => (
      <div
        className={styles.rowWrapper}
        onClick={() =>
          setExpandedState((prev: ExpandedState) => {
            if (row.campaign != null || row.flights != null) {
              const record = prev as Record<string, boolean>;
              record[row.id.toString()] = !record[row.id.toString()];
              return { ...record };
            }
            return prev;
          })
        }
      >
        {tableRow}
      </div>
    ),
    [setExpandedState]
  );

  const tableMeta = useMemo(() => {
    const campaignSums = campaigns?.reduce(
      (sums, c) => {
        const salesData = campaignSalesAggregates?.[c.campaignId];

        sums.currencyCode = c.currencyCode || sums.currencyCode;
        sums.totalBudget += c.totalBudget ?? 0;
        sums.totalSpend += c.totalSpend ?? 0;
        sums.totalSales += salesData?.saleCount ?? 0;
        sums.totalCost += salesData?.totalCost ?? 0;
        sums.totalNetProceeds += salesData?.totalNetProceeds ?? 0;

        sums.activeFlight.budget += c.activeFlight?.flightBudget ?? 0;
        sums.activeFlight.spend += c.activeFlight?.flightSpend ?? 0;

        const activeAggregate =
          salesData?.flightsAggregate?.[c.activeFlight?.flightId ?? 0];
        sums.activeFlight.netProceeds += activeAggregate?.totalNetProceeds ?? 0;
        sums.activeFlight.salesCount += activeAggregate?.saleCount ?? 0;

        const pastFlights = getPastFlightsForCampaign(c);
        if (pastFlights && pastFlights.length > 0) {
          const prevFlight = pastFlights[0];
          sums.previousFlight.budget += prevFlight.flightBudget ?? 0;
          sums.previousFlight.spend += prevFlight.flightSpend ?? 0;
          const previousAggregate =
            salesData?.flightsAggregate?.[prevFlight.flightId ?? 0];
          sums.previousFlight.netProceeds +=
            previousAggregate?.totalNetProceeds ?? 0;
          sums.previousFlight.salesCount += previousAggregate?.saleCount ?? 0;
        }

        return sums;
      },
      {
        currencyCode: 'USD',
        totalBudget: 0,
        totalSpend: 0,
        totalSales: 0,
        totalCost: 0,
        totalNetProceeds: 0,
        activeFlight: {
          budget: 0,
          spend: 0,
          netProceeds: 0,
          salesCount: 0,
        },
        previousFlight: {
          budget: 0,
          spend: 0,
          netProceeds: 0,
          salesCount: 0,
        },
      }
    );

    return {
      summaryData: {
        id: 'summary-total',
        campaignId: 0,
        name: 'Total',
        campaign: {
          campaignId: 0,
          campaignName: 'Total',
          totalBudget: campaignSums?.totalBudget,
          totalSpend: campaignSums?.totalSpend,
          currencyCode: campaignSums?.currencyCode || 'USD',
        },
        flight: {
          flightId: 0,
          flightBudget: campaignSums?.activeFlight.budget,
          flightSpend: campaignSums?.activeFlight.spend,
        },
        flights: [
          {
            flightId: 1,
            flightBudget: campaignSums?.previousFlight.budget,
            flightSpend: campaignSums?.previousFlight.spend,
          },
        ],
        salesData: {
          totalCost: campaignSums?.totalCost,
          totalNetProceeds: campaignSums?.totalNetProceeds,
          saleCount: campaignSums?.totalSales,
          currencyCode: campaignSums?.currencyCode || 'USD',
          flightsAggregate: {
            0: {
              totalNetProceeds: campaignSums?.activeFlight.netProceeds,
              totalCost: 0,
              saleCount: campaignSums?.activeFlight.salesCount,
              currencyCode: campaignSums?.currencyCode || 'USD',
            },
            1: {
              totalNetProceeds: campaignSums?.previousFlight.netProceeds,
              totalCost: 0,
              saleCount: campaignSums?.previousFlight.salesCount,
              currencyCode: campaignSums?.currencyCode || 'USD',
            },
          },
        } as CampaignSalesAggregate,
      } as CampaignsTableRow,
    };
  }, [campaignSalesAggregates, campaigns]);

  if (isLoading) {
    return <PosSpinner />;
  }

  return campaigns?.length ? (
    <ColumnResizingContextProvider>
      <GroupedVirtuosoTable<CampaignsTableRow>
        key={`${hideActive}-${hidePrevious}-${hideOlder}`}
        style={{ height: '100%' }}
        actionColId={'actions'}
        data={tableData}
        columns={columns}
        onExpandedChange={setExpandedState}
        onSortingChange={setSortState}
        getRowId={(data) => String(data.id)}
        getGroupId={(data) => data.campaignId.toString()}
        getSubRows={getSubRows}
        getItemId={(data) => data.id}
        renderGroupAsNormalRow
        isGroupRow={(data) => data.hasChildren}
        rowWrapper={onRowWrapper}
        state={{
          sorting: sortState,
          expanded: expandedState,
        }}
        meta={tableMeta}
      />
    </ColumnResizingContextProvider>
  ) : (
    <NoData style={{ border: `1px solid ${vars.color.borderPrimary}` }}>
      <Content id={ContentId.None} />
    </NoData>
  );
};
